import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { TokenInterceptor } from './token.interceptor';
import { URLInterceptor } from './url.interceptor';

import { APP_INITIALIZER, NgModule } from "@angular/core";
import { Router } from "@angular/router";

import { ReactiveFormsModule } from "@angular/forms";

import { AppComponent } from "./app.component";

import { AppRoutingModule } from "./app-routing.module";
import { AppService } from "./app.service";
import { SiDatavizXlsxService } from "@strategic-insights/ng-dataviz-xlsx";
import { CookieService } from "ngx-cookie";

import {
  SiLoginPageModule,
  SiLoginPageConfig,
  SiLoginDisclaimerConfig,
  SiForgotPasswordConfig,
  SiLoginPageService,
  SiLogoutPageConfig,
} from "@strategic-insights/ng-login-page-kc";

import {
  SiAuthenticationModule,
  SiAuthenticationService,
  ISiAuthenticationConfig,
} from "@strategic-insights/ng-authentication";
import { SharedModule } from "./shared/shared.module";
import { SiKeycloakConfigService } from "./services/si-keycloak-config.service";

import { AuthGuard } from "./auth.guard";
import { AuthTokenGuard } from "./auth-token.gaurd";
import { SiCustomerAccessComponent } from "./si-customer-access/si-customer-access.component";
import config from "../config/config.json";

const siLoginPageConfig: SiLoginPageConfig = new SiLoginPageConfig(
  config["loginPageConfig"]["logoURL"],
  config["loginPageConfig"]["headerText"],
  config["loginPageConfig"]["footerText"],
  config["loginPageConfig"]["styleURL"],
  config["loginPageConfig"]["inviteValidateTokenURL"],
  config["loginPageConfig"]["resetPasswordValidateTokenURL"],
  config["loginPageConfig"]["resetPasswordValidateURL"],
  config["loginPageConfig"]["resetPasswordInitiateURL"],
  config["loginPageConfig"]["resetPasswordURL"],
  config["loginPageConfig"]["activateUserURL"],
  new SiLoginDisclaimerConfig(
    config["loginPageConfig"]["disclaimer"]["isApplied"],
    config["loginPageConfig"]["disclaimer"]["headerText"],
    config["loginPageConfig"]["disclaimer"]["content"],
    config["loginPageConfig"]["disclaimer"]["footerText"]
  ),
  new SiForgotPasswordConfig(
    config["loginPageConfig"]["forgotPassword"]["isApplied"],
    config["loginPageConfig"]["forgotPassword"]["headerText"],
    config["loginPageConfig"]["forgotPassword"]["footerText"],
    config["loginPageConfig"]["forgotPassword"]["branding"]
  ),
  new SiLogoutPageConfig(
    config["loginPageConfig"]["logoutPage"]["logoURL"],
    config["loginPageConfig"]["logoutPage"]["headerText"],
    config["loginPageConfig"]["logoutPage"]["logoutText"],
    config["loginPageConfig"]["logoutPage"]["sessionExpireText"],
    config["loginPageConfig"]["logoutPage"]["footerText"]
  )
);

export function initializeApp(configService: SiKeycloakConfigService) {
  return (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      configService.loadConfig().subscribe(
        () => resolve(),
        error => {
          console.error('Configuration load error:', error);
          reject();
        }
      );
    });
  };
 }

@NgModule({
  declarations: [AppComponent, SiCustomerAccessComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    SharedModule,
    SiAuthenticationModule,
    {
      ngModule: SiLoginPageModule,
      providers: [
        { provide: SiLoginPageConfig, useValue: siLoginPageConfig },
        SiLoginPageService,
      ],
    },
  ],
  providers: [
    SiKeycloakConfigService,
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [SiKeycloakConfigService], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: URLInterceptor, multi: true },
    AuthGuard, AuthTokenGuard, AppService, SiDatavizXlsxService
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private siAuthenticationService: SiAuthenticationService,
    private router: Router,
    private appService: AppService,
    private cookieService: CookieService
  ) {
    const self = this;
    let checkLogin = false;
    let checkSession = false;
    let siAuthenticationConfig: ISiAuthenticationConfig =
      config["authFRConfig"];
    if (this.cookieService.get("authtype")) {
      siAuthenticationConfig = config["authSSOConfig"];
    }
    self.siAuthenticationService.init(siAuthenticationConfig);
    self.siAuthenticationService.authStatus.subscribe((status) => {
      if (status) {
        checkLogin = true;
        const activeCustomer = self.siAuthenticationService.getActiveCustomer();
        if (activeCustomer) {
          self.router.navigate([
            self.siAuthenticationService.getUnformattedCustomerName(
              activeCustomer
            ),
          ]);
        } else if (
          self.siAuthenticationService.getCustomerList().length === 0
        ) {
          self.router.navigate(["/unauthorized"]);
        } else if (
          self.siAuthenticationService.getCustomerList().length === 1
        ) {
          self.siAuthenticationService.setActiveCustomer(
            self.siAuthenticationService.getCustomerList()[0]
          );
        } else {
          self.router.navigate(["customerAccess"]);
        }
      } else if (
        self.siAuthenticationService.getWebToken() ||
        self.siAuthenticationService.getAuthToken()
      ) {
        const userNameCookie = this.cookieService.get("user");
        if (
          userNameCookie &&
          userNameCookie.charAt(0) === '"' &&
          userNameCookie.charAt(userNameCookie.length - 1) === '"'
        ) {
          const updatedUserName = userNameCookie.substr(
            1,
            userNameCookie.length - 2
          );
          this.cookieService.put("user", updatedUserName);
        }
        checkSession = true;
         self.siAuthenticationService.checkTokenNew().subscribe({
            next: (response) => {},
            error: (error) => {
            //  this.router.navigate([CONFIG["loginRoute"]]);
            this.router.navigate(['/kclogoutredirect'], { queryParams: {logout : true}});
            },
          });
      }
      if (!status && checkLogin) {
        checkLogin = false;
      //  this.router.navigate([CONFIG["loginRoute"]]);
        this.router.navigate(['/kclogoutredirect'], { queryParams: {logout : true}});
      }
      else if (!status && checkSession && !(self.siAuthenticationService.getWebToken() || self.siAuthenticationService.getAuthToken())) {
        checkSession = false;
        this.router.navigate(['/kclogoutredirect'], { queryParams: {logout : true}});
      }
    });

    this.siAuthenticationService.activeCustomer.subscribe((customer) => {
      if (customer) {
        this.appService.appLoader.next(true);
        this.router.navigate([customer]);
      }
    });
  }
}
